import { commonDocuments } from "@/api/workItemDrawingInfo/useWorkItemDrawingInfoQuery";
import { COMMON_TEXT } from "@/constants/text";
import useFetcher from "@/hooks/useFetcher";
import { BASE_URL } from "@/instance/axios";
import { usePaginationStore } from "@/store/pagenation.store";
import { useWorkDataStore } from "@/store/work.store";
import { customNotification } from "@/utils/notificationShow";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";

const getDownloadFileName = (
  contentDisposition?: string,
  fallbackName?: string,
) => {
  if (!contentDisposition) {
    return fallbackName ?? "download";
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  if (fileNameMatch?.[1]) {
    return fileNameMatch[1];
  }

  return fallbackName ?? "download";
};

export const useItemDrawingInfoFormFetch = () => {
  const { workData } = useWorkDataStore((state) => ({
    workData: state.workData,
  }));
  const { currentPage } = usePaginationStore((state) => ({
    currentPage: state.currentPage,
  }));

  const itemCode = workData?.work?.item?.code;

  const { queryKey, queryFn } = commonDocuments.get({
    query: {
      $and: [
        {
          relatedCode: {
            $eq: itemCode ?? "",
          },
        },
      ],
    },
    page: currentPage,
    pageSize: 10,
    sort: "-createdAt",
  });

  const { mutateAsync: downLoadDocuments } = useMutation({
    mutationFn: async (req: { keys: string[]; type: string }) => {
      try {
        const response = await axios.post(
          `${BASE_URL}/s3/download`,
          { keys: req.keys, type: req.type },
          {
            responseType: "blob",
            headers: {
              Authorization: "Bearer " + localStorage.getItem("authToken"),
            },
          },
        );

        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], {
                type: response.headers["content-type"],
              });
        const fileName = getDownloadFileName(
          response.headers["content-disposition"],
          req.keys.length === 1 ? req.keys[0] : "attachments.zip",
        );
        const blobUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");

        anchor.href = blobUrl;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(blobUrl);

        return true;
      } catch (error) {
        customNotification.error({
          message: COMMON_TEXT.NOTIFICATION.DOCUMENT_DOWNLOAD_FAIL,
        });
        return false;
      }
    },
  });

  const { data, Fetcher } = useFetcher(
    {
      queryKey,
      queryFn,
    },
    {
      enabled: Boolean(itemCode),
    },
  );

  return { data, Fetcher, itemCode, downLoadDocuments };
};
